// Full-page web portal — the expanded workspace for Jobily.
// Features: Cover Letters, Applications, Job Journey, ATS, Profile, Settings,
//           FAQ, Privacy, Career Coaches, Changelog.

import { useState, useCallback, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useProfileSync } from "@/hooks/useProfileSync";
import { supabase } from "@/integrations/supabase/client";
import {
  Loader2,
  FileText,
  Briefcase,
  User,
  Settings,
  LogOut,
  HelpCircle,
  Shield,
  Mail,
  Sparkles,
  FileSearch,
  Award,
  MapPin,
} from "lucide-react";
import appIcon from "@/assets/app-icon.png";
import { Application, UserProfile } from "@/lib/storage";
import ProfileTab from "@/components/dashboard/ProfileTab";
import ApplicationsTab from "@/components/dashboard/ApplicationsTab";
import CoverLetterTab from "@/components/dashboard/CoverLetterTab";
import SettingsTab from "@/components/dashboard/SettingsTab";
import PageBanner from "@/components/webview/PageBanner";
import AtsResumeTab from "@/components/webview/AtsResumeTab";
import JobJourneyTab from "@/components/webview/JobJourneyTab";
import FaqTab from "@/components/webview/FaqTab";
import ChangelogTab from "@/components/webview/ChangelogTab";
import PrivacyPolicyTab from "@/components/webview/PrivacyPolicyTab";
import PartnersTab from "@/components/webview/PartnersTab";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// --- NAV CONFIGURATION ---
// Main nav (top section)
const MAIN_NAV = [
  { id: "cover", label: "Cover Letter", icon: FileText },
  { id: "applications", label: "Applications", icon: Briefcase },
  { id: "journey", label: "Job Journey", icon: MapPin },
  { id: "ats", label: "ATS Resume", icon: FileSearch },
  { id: "profile", label: "Profile", icon: User },
  { id: "settings", label: "Settings", icon: Settings },
] as const;

// Secondary nav (below divider)
const SECONDARY_NAV = [
  { id: "partners", label: "Career Coaches", icon: Award },
  { id: "faq", label: "FAQ", icon: HelpCircle },
  { id: "privacy", label: "Privacy Policy", icon: Shield },
  { id: "contact", label: "Contact Support", icon: Mail },
] as const;

// Bottom nav (above logout)
const BOTTOM_NAV = [
  { id: "changelog", label: "What's New", icon: Sparkles },
] as const;

type TabId =
  | (typeof MAIN_NAV)[number]["id"]
  | (typeof SECONDARY_NAV)[number]["id"]
  | (typeof BOTTOM_NAV)[number]["id"];

// Page titles for the header
const TAB_TITLES: Record<TabId, string> = {
  cover: "Cover Letter & Q&A",
  applications: "Applications",
  journey: "Job Journey",
  ats: "ATS Resume",
  profile: "Profile",
  settings: "Settings",
  partners: "Career Coaches",
  faq: "FAQ",
  privacy: "Privacy Policy",
  contact: "Contact Support",
  changelog: "What's New",
};

export default function WebViewPage() {
  const { user, loading: authLoading, signOut, subscription } = useAuth();
  const { state, updateState, loading: profileLoading } = useProfileSync();
  const [activeTab, setActiveTab] = useState<TabId>("cover");
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(true);

  // Session handoff — pick up tokens from URL when opened from extension
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const accessToken = params.get("access_token");
    const refreshToken = params.get("refresh_token");

    if (accessToken && refreshToken) {
      supabase.auth
        .setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        })
        .then(({ error }) => {
          if (error) console.error("WebView session handoff failed:", error);
          window.history.replaceState(
            {},
            "",
            window.location.pathname + window.location.hash
          );
          setSessionLoading(false);
        })
        .catch((e) => {
          console.error("WebView session handoff failed:", e);
          window.history.replaceState(
            {},
            "",
            window.location.pathname + window.location.hash
          );
          setSessionLoading(false);
        });
    } else {
      setSessionLoading(false);
    }
  }, []);

  // --- HANDLERS ---
  const handleAddApp = useCallback(
    (app: Application) => {
      updateState((prev) => ({
        ...prev,
        applications: [app, ...prev.applications],
      }));
    },
    [updateState]
  );

  const handleRemoveApp = useCallback(
    (id: string) => {
      updateState((prev) => ({
        ...prev,
        applications: prev.applications.filter((a) => a.id !== id),
      }));
    },
    [updateState]
  );

  const handleUpdateStatus = useCallback(
    (id: string, status: Application["status"]) => {
      updateState((prev) => ({
        ...prev,
        applications: prev.applications.map((a) =>
          a.id === id ? { ...a, status } : a
        ),
      }));
    },
    [updateState]
  );

  const handleUpdateApp = useCallback(
    (id: string, fields: Partial<Application>) => {
      updateState((prev) => ({
        ...prev,
        applications: prev.applications.map((a) =>
          a.id === id ? { ...a, ...fields } : a
        ),
      }));
    },
    [updateState]
  );

  const handleUpdatePersonalField = useCallback(
    (field: keyof UserProfile["personal"], value: string) => {
      updateState((prev) => ({
        ...prev,
        profile: {
          ...prev.profile,
          personal: {
            ...prev.profile.personal,
            [field]: value,
          },
        },
      }));
    },
    [updateState]
  );

  // --- LOADING ---
  if (authLoading || profileLoading || sessionLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center space-y-4">
          <img
            src={appIcon}
            alt="Jobily"
            className="w-12 h-12 rounded-xl mx-auto"
          />
          <h1 className="text-xl font-display font-bold text-foreground">
            Sign in via the extension
          </h1>
          <p className="text-sm text-muted-foreground max-w-xs">
            Open the Jobily browser extension to sign in, then revisit this
            page.
          </p>
        </div>
      </div>
    );
  }

  // Helper to render nav button
  const NavButton = ({
    id,
    label,
    icon: Icon,
    indent = false,
  }: {
    id: TabId;
    label: string;
    icon: typeof FileText;
    indent?: boolean;
  }) => {
    const active = activeTab === id;
    return (
      <button
        type="button"
        onClick={() => setActiveTab(id)}
        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
          indent ? "pl-6" : ""
        } ${
          active
            ? "bg-primary/10 text-primary"
            : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
        }`}
      >
        <Icon className="w-4 h-4" />
        {label}
      </button>
    );
  };

  return (
    <div className="flex min-h-screen bg-background">
      {/* Sidebar */}
      <aside className="w-64 border-r border-border bg-card flex flex-col shrink-0">
        {/* Logo */}
        <div className="px-5 py-5 flex items-center gap-3 border-b border-border">
          <img src={appIcon} alt="Jobily" className="w-9 h-9 rounded-lg" />
          <div>
            <h1 className="font-display font-bold text-base text-foreground tracking-tight">
              Jobily
            </h1>
            <p className="text-[10px] text-muted-foreground">Web Portal</p>
          </div>
        </div>

        {/* Main nav */}
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {MAIN_NAV.map((item) => (
            <NavButton key={item.id} {...item} />
          ))}

          {/* Divider */}
          <div className="py-3">
            <div className="border-t border-border" />
          </div>

          {/* Secondary nav */}
          {SECONDARY_NAV.map((item) => (
            <NavButton key={item.id} {...item} />
          ))}

          {/* Divider */}
          <div className="py-3">
            <div className="border-t border-border" />
          </div>

          {/* Bottom nav */}
          {BOTTOM_NAV.map((item) => (
            <NavButton key={item.id} {...item} />
          ))}
        </nav>

        {/* User footer */}
        <div className="px-4 py-4 border-t border-border space-y-3">
          <div className="text-xs text-muted-foreground truncate">
            {user.email}
          </div>
          <button
            type="button"
            onClick={signOut}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:text-destructive hover:bg-destructive/5 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto">
        {/* Top bar */}
        <header className="sticky top-0 z-10 border-b border-border bg-card/80 backdrop-blur-sm px-8 py-4">
          <h2 className="font-display font-bold text-lg text-foreground">
            {TAB_TITLES[activeTab]}
          </h2>
        </header>

        {/* Content area */}
        <div className="max-w-3xl mx-auto py-6 px-8">
          {/* Banner — shown on all main content pages */}
          {["cover", "applications", "journey", "ats", "profile"].includes(
            activeTab
          ) && <PageBanner />}

          {/* Tab content */}
          {activeTab === "cover" && (
            <CoverLetterTab
              profile={state.profile}
              savedQuestions={state.savedQuestions || []}
              onSaveQuestions={(qs) =>
                updateState((prev) => ({ ...prev, savedQuestions: qs }))
              }
              onAddApplication={handleAddApp}
              onNavigateToPartners={() => setActiveTab("partners")}
            />
          )}
          {activeTab === "applications" && (
            <ApplicationsTab
              applications={state.applications}
              settings={state.settings}
              onAdd={handleAddApp}
              onRemove={handleRemoveApp}
              onUpdateStatus={handleUpdateStatus}
              onUpdateApp={handleUpdateApp}
            />
          )}
          {activeTab === "journey" && (
            <JobJourneyTab
              applications={state.applications}
              isSubscribed={
                !subscription.loading &&
                subscription.subscribed &&
                !subscription.isTrial
              }
            />
          )}
          {activeTab === "ats" && <AtsResumeTab profile={state.profile} />}
          {activeTab === "profile" && (
            <ProfileTab
              profile={state.profile}
              isSignedIn={!!user}
              onSignInOut={signOut}
              onEditDetails={() => setIsEditProfileOpen(true)}
              showExtendedFields
            />
          )}
          {activeTab === "settings" && (
            <SettingsTab
              settings={state.settings}
              onUpdate={(s) =>
                updateState((prev) => ({ ...prev, settings: s }))
              }
            />
          )}
          {activeTab === "partners" && <PartnersTab />}
          {activeTab === "faq" && <FaqTab />}
          {activeTab === "privacy" && <PrivacyPolicyTab />}
          {activeTab === "contact" && (
            <div className="space-y-4 animate-fade-up">
              <div>
                <h3 className="font-display font-bold text-lg text-foreground">
                  Contact Support
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  We&apos;re here to help.
                </p>
              </div>
              <div className="rounded-xl border border-border bg-card p-6 text-center space-y-3">
                <Mail className="w-8 h-8 mx-auto text-primary" />
                <p className="text-sm text-foreground">
                  For questions, feedback, or issues:
                </p>
                <a
                  href="mailto:support@squareoneinternational.com"
                  className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline"
                >
                  <Mail className="w-4 h-4" />
                  support@squareoneinternational.com
                </a>
                <p className="text-xs text-muted-foreground mt-2">
                  We typically respond within 24 hours.
                </p>
              </div>
            </div>
          )}
          {activeTab === "changelog" && <ChangelogTab />}
        </div>
      </main>

      {/* Edit Profile Dialog */}
      <Dialog open={isEditProfileOpen} onOpenChange={setIsEditProfileOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Profile Details</DialogTitle>
            <p className="text-sm text-muted-foreground">
              These fields power autofill on job applications. The more you
              fill, the less you type.
            </p>
          </DialogHeader>

          <div className="space-y-6 py-2">
            {/* Core Info */}
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                Core Info
              </h4>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="fullName">Full Name</Label>
                  <Input
                    id="fullName"
                    value={state.profile.personal.fullName}
                    onChange={(e) =>
                      handleUpdatePersonalField("fullName", e.target.value)
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={state.profile.personal.email}
                    onChange={(e) =>
                      handleUpdatePersonalField("email", e.target.value)
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={state.profile.personal.phone}
                    onChange={(e) =>
                      handleUpdatePersonalField("phone", e.target.value)
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="location">Location (single line)</Label>
                  <Input
                    id="location"
                    value={state.profile.personal.location}
                    onChange={(e) =>
                      handleUpdatePersonalField("location", e.target.value)
                    }
                    placeholder="San Francisco, CA, USA"
                  />
                </div>
              </div>
            </div>

            {/* Address */}
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                Address
              </h4>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <Label htmlFor="addressLine1">Address Line 1</Label>
                  <Input
                    id="addressLine1"
                    value={state.profile.personal.addressLine1 || ""}
                    onChange={(e) =>
                      handleUpdatePersonalField(
                        "addressLine1",
                        e.target.value
                      )
                    }
                    placeholder="123 Main Street"
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="addressLine2">Address Line 2</Label>
                  <Input
                    id="addressLine2"
                    value={state.profile.personal.addressLine2 || ""}
                    onChange={(e) =>
                      handleUpdatePersonalField(
                        "addressLine2",
                        e.target.value
                      )
                    }
                    placeholder="Apt 4B"
                  />
                </div>
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={state.profile.personal.city}
                    onChange={(e) =>
                      handleUpdatePersonalField("city", e.target.value)
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="state">State / Province</Label>
                  <Input
                    id="state"
                    value={state.profile.personal.state}
                    onChange={(e) =>
                      handleUpdatePersonalField("state", e.target.value)
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="postcode">Postcode / ZIP</Label>
                  <Input
                    id="postcode"
                    value={state.profile.personal.postcode}
                    onChange={(e) =>
                      handleUpdatePersonalField("postcode", e.target.value)
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={state.profile.personal.country}
                    onChange={(e) =>
                      handleUpdatePersonalField("country", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>

            {/* Personal Details */}
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                Personal Details
              </h4>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="dateOfBirth">Date of Birth</Label>
                  <Input
                    id="dateOfBirth"
                    type="date"
                    value={state.profile.personal.dateOfBirth || ""}
                    onChange={(e) =>
                      handleUpdatePersonalField(
                        "dateOfBirth",
                        e.target.value
                      )
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="gender">Gender</Label>
                  <Select
                    value={state.profile.personal.gender || ""}
                    onValueChange={(val) =>
                      handleUpdatePersonalField("gender", val)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="non-binary">Non-binary</SelectItem>
                      <SelectItem value="prefer-not-to-say">
                        Prefer not to say
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="nationality">Nationality</Label>
                  <Input
                    id="nationality"
                    value={state.profile.personal.nationality}
                    onChange={(e) =>
                      handleUpdatePersonalField(
                        "nationality",
                        e.target.value
                      )
                    }
                  />
                </div>
                <div>
                  <Label htmlFor="workAuthorization">
                    Work Authorization
                  </Label>
                  <Select
                    value={state.profile.personal.workAuthorization || ""}
                    onValueChange={(val) =>
                      handleUpdatePersonalField("workAuthorization", val)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="citizen">Citizen</SelectItem>
                      <SelectItem value="permanent-resident">
                        Permanent Resident
                      </SelectItem>
                      <SelectItem value="work-visa">Work Visa</SelectItem>
                      <SelectItem value="student-visa">
                        Student Visa
                      </SelectItem>
                      <SelectItem value="requires-sponsorship">
                        Requires Sponsorship
                      </SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Professional */}
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                Professional
              </h4>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="seniorityLevel">Seniority Level</Label>
                  <Select
                    value={state.profile.personal.seniorityLevel || ""}
                    onValueChange={(val) =>
                      handleUpdatePersonalField("seniorityLevel", val)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="intern">Intern</SelectItem>
                      <SelectItem value="entry">Entry Level</SelectItem>
                      <SelectItem value="mid">Mid Level</SelectItem>
                      <SelectItem value="senior">Senior</SelectItem>
                      <SelectItem value="lead">Lead</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="director">Director</SelectItem>
                      <SelectItem value="executive">Executive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="yearsOfExperience">
                    Years of Experience
                  </Label>
                  <Input
                    id="yearsOfExperience"
                    value={
                      state.profile.personal.yearsOfExperience || ""
                    }
                    onChange={(e) =>
                      handleUpdatePersonalField(
                        "yearsOfExperience",
                        e.target.value
                      )
                    }
                    placeholder="e.g. 5"
                  />
                </div>
                <div>
                  <Label htmlFor="timeZone">Time Zone</Label>
                  <Input
                    id="timeZone"
                    value={state.profile.personal.timeZone}
                    onChange={(e) =>
                      handleUpdatePersonalField("timeZone", e.target.value)
                    }
                    placeholder="America/Los_Angeles (PST)"
                  />
                </div>
                <div>
                  <Label htmlFor="github">GitHub</Label>
                  <Input
                    id="github"
                    value={state.profile.personal.github || ""}
                    onChange={(e) =>
                      handleUpdatePersonalField("github", e.target.value)
                    }
                    placeholder="github.com/username"
                  />
                </div>
                <div>
                  <Label htmlFor="linkedIn">LinkedIn</Label>
                  <Input
                    id="linkedIn"
                    value={state.profile.personal.linkedIn}
                    onChange={(e) =>
                      handleUpdatePersonalField("linkedIn", e.target.value)
                    }
                    placeholder="linkedin.com/in/username"
                  />
                </div>
                <div>
                  <Label htmlFor="portfolio">Portfolio Website</Label>
                  <Input
                    id="portfolio"
                    value={state.profile.personal.portfolio}
                    onChange={(e) =>
                      handleUpdatePersonalField("portfolio", e.target.value)
                    }
                    placeholder="yoursite.com"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setIsEditProfileOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
